
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar as CalendarIcon, DollarSign } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";

const opportunityStages = ["Qualified", "Proposal", "Negotiation", "Closed Won", "Closed Lost"];

export function OpportunityForm() {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [value, setValue] = useState("");
  const [stage, setStage] = useState("");
  const [expectedCloseDate, setExpectedCloseDate] = useState<Date | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Get organization_id from URL if present
  const params = new URLSearchParams(location.search);
  const organizationId = params.get('organization');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    
    if (!name || !stage) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { error } = await supabase.from("opportunities").insert({
        name,
        description,
        stage,
        value: value ? parseFloat(value) : null,
        expected_close_date: expectedCloseDate?.toISOString().split('T')[0],
        organization_id: organizationId || undefined,
        owner_id: user?.id || undefined,
        created_at: new Date().toISOString(),
      });
      
      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Opportunity was created successfully",
      });
      
      // Navigate back to opportunities list or to the related organization
      if (organizationId) {
        navigate(`/organizations/${organizationId}?tab=opportunities`);
      } else {
        navigate("/opportunities");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create opportunity",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Opportunity name"
            required
          />
        </div>
        
        <div>
          <Label htmlFor="stage">Stage *</Label>
          <Select value={stage} onValueChange={setStage} required>
            <SelectTrigger>
              <SelectValue placeholder="Select opportunity stage" />
            </SelectTrigger>
            <SelectContent>
              {opportunityStages.map((stageOption) => (
                <SelectItem key={stageOption} value={stageOption}>
                  {stageOption}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div>
          <Label htmlFor="value">Value</Label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              id="value"
              type="number"
              min="0"
              step="0.01"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="0.00"
              className="pl-9"
            />
          </div>
        </div>
        
        <div>
          <Label htmlFor="expectedCloseDate">Expected Close Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="expectedCloseDate"
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !expectedCloseDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {expectedCloseDate ? format(expectedCloseDate, "PPP") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={expectedCloseDate}
                onSelect={setExpectedCloseDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add details about this opportunity"
            className="min-h-32"
          />
        </div>
      </div>
      
      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/opportunities")}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Creating..." : "Create Opportunity"}
        </Button>
      </div>
    </form>
  );
}
